import GenericApi from "@/api/genericApi";
import {
  CryptoAccountQueryKey,
  CryptoDepositQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import {
  CryptoAccount,
  CryptoDeposit,
  CryptoDepositResponse,
} from "@/types/BankSettings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useRef, useState } from "react";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { QRCodeCanvas } from "qrcode.react";
import { isBitramp } from "@/utils/utils";

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DepositModal: FC<DepositModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState<string | "">("");
  const [selectedCurrency, setSelectedCurrency] = useState<string>("-");
  const [selectedWallet, setSelectedWallet] = useState<string>("-");
  const [selectedNetwork, setSelectedNetwork] = useState<string>("-");
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [QRCode, setQRCode] = useState<boolean>(false);
  const walletAddressRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const { data: cryptoAccounts } = useQuery<ApiResponse<CryptoAccount[]>>({
    queryKey: [CryptoAccountQueryKey],
    queryFn: async () =>
      GenericApi.get<CryptoAccount[]>(`${ApiRoutes.CRYPTOACCOUNT}`),
  });

  const updateCryptoDepositMutation = useMutation<
    ApiResponse<CryptoDepositResponse>,
    ApiError<ValidationErrors>,
    CryptoDeposit
  >({
    mutationFn: (data) =>
      GenericApi.post<CryptoDepositResponse>(ApiRoutes.CRYPTODEPOSIT, data),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);

      if (data?.wallet_address) {
        setWalletAddress(data.wallet_address);
      }

      await queryClient.invalidateQueries({
        queryKey: [CryptoDepositQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const handleCryptoDeposit = () => {
    if (!amount) {
      CustomLogger.error("Please select a wallet and enter an amount.");
      return;
    }

    const payload = {
      currency: selectedCurrency,
      network:
        selectedCurrency === "USDT" || selectedCurrency === "USTC"
          ? selectedNetwork
          : "",
      amount: Number(amount),
      from_crypto_account: selectedWallet,
    };

    updateCryptoDepositMutation.mutate(payload);
  };

  const copyWalletAddressRefToClipboard = () => {
    if (walletAddressRef.current) {
      navigator.clipboard
        .writeText(walletAddress ? walletAddress : "")
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
    }
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
      setQRCode(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full"
      onClick={handleCloseModal}
    >
      <div
        className={`${
          isBitramp() ? "bg-[#2B2B2B] border border-[#2B2B2B]" : "bg-white"
        } min-w-[30rem] h-auto  rounded-[1.5rem] text-center relative`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => {
            onClose();
            setQRCode(false);
          }}
          className="absolute top-4 right-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
          >
            <path
              d="M17 17L2 2"
              stroke={isBitramp() ? "#fff" : "#202A43"}
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M2 17L17 2"
              stroke={isBitramp() ? "#fff" : "#202A43"}
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div
          className={`${
            isBitramp() ? "text-[#fff]" : "text-[#05004E]"
          } pt-[2.5rem] text-center text-[1.5rem] font-[700]`}
        >
          {QRCode ? "QR code" : "Deposit"}
        </div>

        <div
          className={`${
            isBitramp() ? "text-[#9F9F9F]" : "text-[#737791]"
          } text-[1rem] w-[17rem] ml-[7rem] pt-[1rem] font-[500] text-center`}
        >
          {QRCode
            ? "Finish the process quickly by scanning the code"
            : "Add here the amount of money you want to deposit"}
        </div>

        {QRCode ? (
          <div className="flex flex-col items-center justify-center mb-6">
            {walletAddress && (
              <div className="flex flex-col items-center justify-center pt-4">
                {isBitramp() ? (
                  <QRCodeCanvas
                    bgColor="#2B2B2B"
                    fgColor="#FFFFFF"
                    value={walletAddress}
                    size={260}
                  />
                ) : (
                  <QRCodeCanvas
                    value={walletAddress}
                    size={260}
                  />
                )}

                <hr
                  className={`${
                    isBitramp() ? "border-[#464646]" : "border-[#202A43]"
                  } w-[30rem] my-[3rem]`}
                />
                <div className="text-[#C0C0C0] text-[1rem] font-satoshi">
                  Copy wallet address
                </div>
                <div className="flex justify-center items-center gap-4 ml-[1.5rem] w-full py-4">
                  {/* <div
                    className="text-[#202A43] text-[1.1875rem] font-[700] font-satoshi"
                    ref={walletAddressRef}
                  >
                    {copied ? "Copied!" : walletAddress}
                  </div> */}
                  <div
                    className={`${
                      isBitramp() ? "text-[#fff]" : "text-[#202A43]"
                    } text-[1rem] font-satoshi`}
                    ref={walletAddressRef}
                  >
                    {copied ? (
                      "Copied!"
                    ) : (
                      <>
                        {walletAddress.slice(0, 21)}
                        <br />
                        {walletAddress.slice(21)}
                      </>
                    )}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="21.13"
                    viewBox="0 0 23 27"
                    fill="none"
                    onClick={copyWalletAddressRefToClipboard}
                    className="cursor-pointer"
                  >
                    <path
                      d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                      fill={isBitramp() ? "#8AFEFD" : "#222B45"}
                    />
                    <path
                      d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                      fill={isBitramp() ? "#8AFEFD" : "#222B45"}
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center">
            <div className="relative w-[22rem] mt-[1rem]">
              <select
                className={`w-full p-3 border ${
                  isBitramp()
                    ? "bg-[#2B2B2B] border border-[#464646] text-white focus:border-[#464646] focus:ring-0"
                    : "bg-white border border-[#D7D7D7] text-black"
                } rounded-[0.7rem] appearance-none`}
                value={selectedWallet}
                onChange={(e) => {
                  const selectedWalletId = e.target.value;
                  setSelectedWallet(selectedWalletId);

                  const wallet = cryptoAccounts?.data?.find(
                    (cryptoAccount) =>
                      cryptoAccount.id === Number(selectedWalletId)
                  );

                  if (wallet) {
                    setSelectedCurrency(wallet.currency);
                  }
                }}
              >
                <option value="-" disabled>
                  Select wallet
                </option>
                {cryptoAccounts?.data?.map((cryptoAccount) => (
                  <option key={cryptoAccount.id} value={cryptoAccount.id}>
                    {cryptoAccount.name}
                  </option>
                ))}
              </select>

              <div className="absolute top-1/2 right-[0.66rem] transform -translate-y-1/2 pointer-events-none rotate-180">
                <svg
                  width="17.8"
                  height="15.8"
                  viewBox="0 0 13 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`transition-transform duration-300 ${
                    isOpen ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <path
                    d="M7.35044 9.6252C6.95955 10.2571 6.04045 10.2571 5.64956 9.6252L0.948823 2.02607C0.536717 1.35987 1.0159 0.5 1.79926 0.5L11.2007 0.500001C11.9841 0.500001 12.4633 1.35987 12.0512 2.02607L7.35044 9.6252Z"
                    fill={isBitramp() ? "#fff" : "#222B45"}
                  />
                </svg>
              </div>
            </div>

            {selectedCurrency === "USDT" || selectedCurrency === "USTC" ? (
              <div className="relative w-[22rem]">
                <select
                  className={`w-full p-3 border mt-[1rem] ${
                    isBitramp()
                      ? "bg-[#2B2B2B] border border-[#464646] text-white focus:border-[#464646] focus:ring-0"
                      : "bg-white border border-[#D7D7D7] text-black"
                  } rounded-[0.7rem] appearance-none`}
                  value={selectedNetwork}
                  onChange={(e) => setSelectedNetwork(e.target.value)}
                >
                  <option value="-" disabled>
                    Select network
                  </option>
                  <option value="ERC20">ERC20</option>
                  <option value="TRC20">TRC20</option>
                </select>

                <div className="absolute mt-[0.5rem] top-1/2 right-[0.66rem] transform -translate-y-1/2 pointer-events-none rotate-180">
                  <svg
                    width="17.8"
                    height="15.8"
                    viewBox="0 0 13 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`transition-transform duration-300 ${
                      isOpen ? "rotate-180" : "rotate-0"
                    }`}
                  >
                    <path
                      d="M7.35044 9.6252C6.95955 10.2571 6.04045 10.2571 5.64956 9.6252L0.948823 2.02607C0.536717 1.35987 1.0159 0.5 1.79926 0.5L11.2007 0.500001C11.9841 0.500001 12.4633 1.35987 12.0512 2.02607L7.35044 9.6252Z"
                      fill={isBitramp() ? "#fff" : "#222B45"}
                    />
                  </svg>
                </div>
              </div>
            ) : (
              <div></div>
            )}

            <div className="relative w-[22rem] pt-[1rem]">
              <input
                type="text"
                placeholder="5000"
                className={`w-full p-3 pr-10 ${
                  isBitramp()
                    ? "bg-[#2B2B2B] border border-[#464646] text-white focus:border-[#464646] focus:ring-0 placeholder:text-[rgba(115, 119, 145, 0.34)]"
                    : "border border-[#D7D7D7] placeholder:text-[#D3D3D3]"
                } rounded-[0.7rem] `}
                onChange={(e) => {
                  let val = e.target.value.replace(",", ".");

                  if (val.startsWith("00")) {
                    val = "0.0";
                  }

                  if (/^\d*\.?\d*$/.test(val)) {
                    setAmount(val);
                  }
                }}
                value={amount}
              />
              <span
                className={`${
                  isBitramp() ? "text-white" : "text-[#222B45]"
                } absolute right-3 top-[2.5rem] -translate-y-1/2 text-[1rem] cursor-pointer`}
              >
                {selectedCurrency}
              </span>
            </div>
          </div>
        )}

        {QRCode ? (
          <div></div>
        ) : (
          <button
            className={`${
              isBitramp()
                ? "Primary_button mb-[3rem] mt-[3rem] ml-[10rem] !w-[9.3125rem] !h-[2.8125rem]"
                : "text-[#F9F9F9] mb-[3rem] mt-[3rem] ml-[9.5rem] text-center text-[1.1875rem] font-[700] buttonModal"
            }`}
            onClick={() => {
              handleCryptoDeposit();
              setQRCode(true);
            }}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default DepositModal;
